import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { CartItemDesign } from "../models/CartItem";
import { ProductPreviewDesign } from "./ProductPreviewDesign";
import {
  PriceLine,
  getUniqueSortedPrices,
} from "../../../components/Logo/LogoPrices";
import { useDesignActionHandler } from "../../design-list/hooks/useDesignActionHandler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone } from "@fortawesome/free-solid-svg-icons";
import { getDesignProductPrice } from "../../../../core/models/entities/BaseLogo";
import { canCloneDesign } from "../../../../core/models/entities/Design";
import { useTranslation } from "react-i18next";

interface DesignDetailsProps {
  orderline: CartItemDesign;
}

export function DesignDetails({ orderline }: DesignDetailsProps) {
  const { t } = useTranslation();
  const designName = orderline.design.title;
  const manufacturer =
    orderline.design.style?.manufacturer?.name ||
    orderline.design.navManufacturerName;
  const styleModel =
    orderline.design.style?.model || orderline.design.navStyleName;
  const customer = orderline.design.customer?.name;
  const prices = DesignDetailsPrices(orderline);

  const actionHandler = useDesignActionHandler();

  function handleCopy() {
    actionHandler.handleCopyDesign(orderline.design).then((d) => {
      // justCloned: same as the design-list clone flow — the editor shows a
      // neutral "preparing" state instead of an error flash while the fresh
      // clone's first GetDesign heals (design-editor2.screen).
      actionHandler.handleGoToUpdateDesign(d, undefined, { justCloned: true });
    });
  }

  return (
    <div className={"space-y-2 w-full"}>
      <p className={"text-sm font-semibold"} style={{ lineHeight: "18px" }}>
        {t("orderCreate.product")}
      </p>
      <div className={"flex border rounded w-full space-x-5"}>
        <ProductPreviewDesign orderline={orderline} size={"lg"} />
        <div className={"flex flex-1 flex-col bg-gray-100 p-3"}>
          <div className={"text-xs p-1 flex flex-col gap-1"}>
            <div>
              <b>{t("item_no")}</b>{" "}
              <a
                href={`/design?searchTerm=${orderline.design.product?.mul}&pageSize=25&pageIndex=1`}
                className={"hover:underline"}
              >
                {orderline.design.product?.mul}
              </a>
            </div>
            <p>
              <b>{t("name")}</b> {designName}
            </p>
            <p>
              <b>{t("manufacturer")}</b> {manufacturer}
            </p>
            <p>
              <b>{t("model")}</b> {styleModel}
            </p>
            {orderline.design.customer?.type !== "vendor" && (
              <p>
                <b>{t("customer")}</b> {customer}
              </p>
            )}
          </div>
          <div className={"flex-1 text-xs pl-1 mt-2"}>
            <p>
              <b>{t("orderCreate.designPrice")}</b>
            </p>
            {prices}
          </div>
          <div className={"flex-1"} />
          {/* Same rule as the design-list popup: Clone must not be gated on
              isEditable (the nightly NAV import locks every design that has been
              through NAV — the API births clones editable). Show only when the clone
              will actually land: the API reports canClone (true only when a garment
              resolves); canCloneDesign falls back to the previous style/navStyleName
              gate when that verdict is absent (older API / flag off / cart design
              without it), so nothing that used to be cloneable disappears. */}
          {canCloneDesign(orderline.design) && (
            <PrimaryButton
              width={"full"}
              className={"uppercase px-4"}
              onClick={handleCopy}
            >
              <FontAwesomeIcon icon={faClone} />
              <span className="flex-1">{t("orderCreate.cloneDesign")}</span>
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}

export function DesignDetailsPrices({ design, instances }: CartItemDesign) {
  const { t } = useTranslation();
  if (
    design.product?.totalPrices === undefined ||
    design.product.totalPrices.length == 0
  ) {
    return <p className={"font-bold"}>{t("orderCreate.noAvailablePrices")}</p>;
  }

  const sortedPrices = getUniqueSortedPrices(design.product.totalPrices);

  // A design appears at most once in the cart, so its tier total is just the
  // sum of its instances' delivery + storage. NaN-safe in case a row carries
  // a temporarily-empty input.
  const totalQuantity = (instances ?? []).reduce(
    (sum, inst) =>
      sum +
      (Number.isFinite(inst.toDelivery) ? inst.toDelivery : 0) +
      (Number.isFinite(inst.toStorage) ? inst.toStorage : 0),
    0,
  );
  const highlightedMinQuantity =
    totalQuantity > 0
      ? getDesignProductPrice(design.product.totalPrices, totalQuantity)
          ?.minQuantity
      : undefined;

  const midpoint = Math.ceil(sortedPrices.length / 2);
  const firstHalf = sortedPrices.slice(0, midpoint);
  const secondHalf = sortedPrices.slice(midpoint);

  const renderPrices = (prices: typeof sortedPrices) => {
    return prices.map((p) => {
      const key = p.minQuantity;
      return (
        <span
          key={key}
          className={`pt-1 text-xs capitalize ${
            highlightedMinQuantity === p.minQuantity ? "font-bold" : ""
          }`}
        >
          {PriceLine({ price: p })}
        </span>
      );
    });
  };

  return (
    <div className={"grid grid-cols-2 gap-0 mb-4"}>
      <div className="flex flex-col">{renderPrices(firstHalf)}</div>
      <div className="flex flex-col">{renderPrices(secondHalf)}</div>
    </div>
  );
}
